/**
 * Performance monitoring utilities
 * Tracks render times, WebSocket performance, and user experience metrics
 */

import { logger } from "./logger";

/**
 * Get PostHog instance for analytics
 */
function getPostHog(): unknown {
  try {
    if (typeof window !== "undefined") {
      const win = window as unknown as { posthog?: unknown };
      if (win.posthog) {
        return win.posthog;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
}

/**
 * Performance metric interface
 */
export interface PerformanceMetric {
  name: string;
  value: number;
  unit?: string;
  tags?: Record<string, string | number>;
}

/**
 * Track a performance metric
 */
export function trackPerformanceMetric(metric: PerformanceMetric): void {
  const posthog = getPostHog();
  if (
    posthog &&
    typeof posthog === "object" &&
    "capture" in posthog &&
    typeof (posthog as { capture: unknown }).capture === "function"
  ) {
    (
      posthog as {
        capture: (event: string, props: Record<string, unknown>) => void;
      }
    ).capture("performance_metric", {
      metric_name: metric.name,
      metric_value: metric.value,
      metric_unit: metric.unit || "ms",
      ...metric.tags,
    });
  }

  // Log in development
  if (import.meta.env.DEV) {
    logger.debug(`[Performance] ${metric.name}`, {
      value: metric.value,
      unit: metric.unit || "ms",
      ...metric.tags,
    });
  }
}

/**
 * Measure render time for a component or function
 */
/** @lintignore - Utility function, kept for future use */
export function measureRenderTime<T>(
  name: string,
  fn: () => T,
  tags?: Record<string, string | number>
): T {
  const startTime = performance.now();
  try {
    const result = fn();

    // If it's a promise, measure async
    if (result instanceof Promise) {
      result
        .then(() => {
          const duration = performance.now() - startTime;
          trackPerformanceMetric({
            name: `${name}_async`,
            value: duration,
            tags,
          });
        })
        .catch((error) => {
          logger.error(`[Performance] Error in ${name}`, error);
        });
      return result;
    }

    // Synchronous execution
    const duration = performance.now() - startTime;
    trackPerformanceMetric({
      name,
      value: duration,
      tags,
    });

    return result;
  } catch (error) {
    logger.error(`[Performance] Error measuring ${name}`, error);
    throw error;
  }
}

/**
 * Create a performance mark and measure
 */
/** @lintignore - Utility function, kept for future use */
export function markStart(name: string): void {
  if (typeof performance !== "undefined" && performance.mark) {
    performance.mark(`${name}-start`);
  }
}

/** @lintignore - Utility function, kept for future use */
export function markEnd(
  name: string,
  tags?: Record<string, string | number>
): void {
  if (
    typeof performance !== "undefined" &&
    performance.mark &&
    performance.measure
  ) {
    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);

      const measure = performance.getEntriesByName(name, "measure")[0];
      if (measure) {
        trackPerformanceMetric({
          name,
          value: measure.duration,
          tags,
        });

        // Clean up marks and measures
        performance.clearMarks(`${name}-start`);
        performance.clearMarks(`${name}-end`);
        performance.clearMeasures(name);
      }
    } catch (error) {
      logger.error(`[Performance] Error marking end for ${name}`, error);
    }
  }
}

/**
 * Track WebSocket performance metrics
 */
export function trackWebSocketMetric(
  metric:
    | "connection_time"
    | "message_latency"
    | "reconnect_delay"
    | "queue_size",
  value: number,
  tags?: Record<string, string | number>
): void {
  trackPerformanceMetric({
    name: `websocket_${metric}`,
    value,
    tags: {
      ...tags,
      component: "websocket",
    },
  });
}

/**
 * Track component render time
 */
export function trackComponentRender(
  componentName: string,
  renderTime: number,
  props?: Record<string, unknown>
): void {
  trackPerformanceMetric({
    name: "component_render",
    value: renderTime,
    tags: {
      component: componentName,
      ...(props && Object.keys(props).length > 0
        ? { props_count: Object.keys(props).length }
        : {}),
    },
  });
}

/**
 * Track user experience metrics
 */
/** @lintignore - Utility function, kept for future use */
export function trackUXMetric(
  metric:
    | "page_load"
    | "interaction_delay"
    | "action_completion"
    | "error_recovery",
  value: number,
  tags?: Record<string, string | number>
): void {
  trackPerformanceMetric({
    name: `ux_${metric}`,
    value,
    tags: {
      ...tags,
      category: "user_experience",
    },
  });
}

/**
 * Hook to measure render time (for React components)
 */
/** @lintignore - Utility function, kept for future use */
export function useRenderTime(componentName: string): void {
  if (typeof window === "undefined") return;

  const renderStart = performance.now();

  // Use requestIdleCallback or setTimeout to measure after render
  if (typeof requestIdleCallback !== "undefined") {
    requestIdleCallback(() => {
      const renderTime = performance.now() - renderStart;
      trackComponentRender(componentName, renderTime);
    });
  } else {
    setTimeout(() => {
      const renderTime = performance.now() - renderStart;
      trackComponentRender(componentName, renderTime);
    }, 0);
  }
}
